import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { AnimatedPanel, Card, SectionTitle, CardsContainer } from "./ui/StyledUI";
import { ReactComponent as SidebarButtonIcon } from "../../../../assets/images/icons/Sidebar.svg";
import { ReactComponent as EditNewIcon } from "../../../../assets/images/icons/EditNewIcon.svg";
import Modal from "../../components/Modal";
import UploadModal from "./components/UploadModal";
import { putPersonalizationItem } from "../../../../api/RoomsController";
import { uploadFile } from "../../../../api/FilesController";
import Banner from "../../../../components/layout/ui/Banner";
import * as constants from "../../../../constants";
import { socket } from "../../../../socket";

const getFramesWithImages = (data) => {
    return [
        {
            id: "4c625636-62c7-48a1-b06b-fba054050dcc",
            name: "intraportPreview",
            label: "Intraport Preview",
            img: data?.previewImg?.url ?? "https://picsum.photos/seed/mf1/600/400",
            desc: "Update how your place is presented in the Intraport.",
        },
        {
            id: "4c625636-62c7-48a1-b06b-fba054050dcc",
            name: "squareImageFrame",
            label: "Square Image Frame",
            img: data?.squareImg?.url ?? "https://picsum.photos/seed/mf2/600/400",
            desc: "Update the square image frame located on the first floor.",
        },
        {
            id: "255d591c-3aca-4224-97d1-59d76020c5db",
            name: "verticalImageFrame1",
            label: "Vertical Image Frame 1",
            img: data?.verticalImg1?.url ?? "https://picsum.photos/seed/mf3/600/800",
            desc: "Update the vertical image frame located on the first floor.",
        },
        {
            id: "f9856863-2087-40e0-bd9f-64247c128dfc",
            name: "verticalImageFrame2",
            label: "Vertical Image Frame 2",
            img: data?.verticalImg2?.url ?? "https://picsum.photos/seed/mf4/600/800",
            desc: "Update the vertical image frame located on the second floor.",
        },
        {
            id: "07fdecd0-fcb1-4e4a-818d-c669ce3153d6",
            name: "videoFrame",
            label: "Video Frame",
            img: data?.videoFrameImg?.url ?? "https://picsum.photos/seed/mf5/600/400",
            desc: "Update the video frame located on the second floor.",
        },
    ];
};

export default function MediaFrames({
    selected,
    setSelectedSection,
    onSelectOption = () => {},
    imagesAndVideosUrls,
    setImagesAndVideosUrls,
    token,
    roomId,
    userId,
    isSocketRequest,
    setIsSocketRequest,
}) {
    const user = useSelector((state) => state.user);
    const [editClicked, setEditClicked] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(null);
    const [editSelectedFrame, setEditSelectedFrame] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const frameRefs = useRef({});
    const FRAMES = getFramesWithImages(imagesAndVideosUrls);

    const frameKeyMap = {
        verticalImageFrame1: "verticalImg1",
        verticalImageFrame2: "verticalImg2",
        squareImageFrame: "squareImg",
        intraportPreview: "previewImg",
        videoFrame: "videoFrameImg",
    };

    useEffect(() => {
        if (selected && frameRefs.current[selected]) {
            const el = frameRefs.current[selected];
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            setHighlighted(selected);
        }
    }, [selected]);

    useEffect(() => {
        if (isSocketRequest) {
            setIsModalOpen(true);
            setIsSocketRequest(false);
            setEditSelectedFrame(FRAMES.find((f) => f.name === selected) || null);
        }
    }, [isSocketRequest, setIsSocketRequest]);

    const onSubmitUploadedMedia = async (frameId, file) => {
        setIsModalOpen(false);
        let response = await uploadFile(
            { file, usageType: "roomDetailsImage", roomId, targetUserId: userId },
            user.token
        );

        if (response.status === 200) {
            const fileId = response.data.id;
            response = await putPersonalizationItem(
                frameId,
                {
                    isExternalUrl: false,
                    externalUrl: null,
                    fileId,
                },
                token
            );

            if (response.status === 204 || response.status === 200) {
                setSuccessMessage("Changes are live in Intraverse!");
                setIsModalOpen(false);
                window.location.reload();
            }
        } else {
            // runBanner("Error Uploading File");
            console.log("Error updating personalization item");
            setIsModalOpen(false);
        }
    };

    const handleEditClick = (obj) => {
        setIsModalOpen(true);
        onSelectOption(obj.id);
        setEditSelectedFrame({
            ...obj,
        });
    };

    const handleClickBack = () => {
        onSelectOption(null);
        setSelectedSection("Overview");
        setHighlighted(null);
    };

    return (
        <>
            <Banner
                width={"100%"}
                zIndex="1000"
                message={`${successMessage}`}
                type={"SUCCESS"}
                openBanner={!!successMessage}
                setOpenBanner={setSuccessMessage}
                topDisplay
                showCloseButton
                gameScreen={false}
                timer={8000}
                top={"78px"}
                left={"0"}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <StyledSidebarButtonIcon onClick={handleClickBack} />
                    <div>
                        <SectionTitle>Media Frames</SectionTitle>
                    </div>
                </div>
                {FRAMES.map((f) => (
                    <AnimatedPanel
                        key={f.name}
                        ref={(el) => {
                            frameRefs.current[f.name] = el;
                        }}
                        style={{
                            boxShadow: highlighted === f.name ? "0 0 12px rgba(180, 140, 255, 0.6)" : "none",
                            transition: "box-shadow 0.4s ease",
                        }}
                    >
                        <PanelContent>
                            <LeftSide>
                                <CardLabel>{f.label}</CardLabel>
                                <Description>{f.desc}</Description>
                                <CardMedia>
                                    <BoxDescription>Default Intraverse Media</BoxDescription>
                                    <StyledEditNewIcon onClick={() => handleEditClick(f)} />
                                </CardMedia>
                            </LeftSide>
                            <ThinVL />
                            <RightSide>
                                <CardImage
                                    src={f.img}
                                    alt={f.label}
                                    style={{
                                        height: f.name.includes("vertical") ? "250px" : "150px",
                                        objectFit: "cover",
                                    }}
                                />
                            </RightSide>
                        </PanelContent>
                    </AnimatedPanel>
                ))}
            </div>
            <Modal
                isOpen={isModalOpen}
                setIsOpen={setIsModalOpen}
                children={
                    <UploadModal
                        selected={selected}
                        setIsModalOpen={setIsModalOpen}
                        editSelectedFrame={editSelectedFrame}
                        onSubmitUploadedMedia={onSubmitUploadedMedia}
                    />
                }
            />
        </>
    );
}

const CardLabel = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #e6dfff;
`;

const BoxDescription = styled.div`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
`;

const StyledSidebarButtonIcon = styled(SidebarButtonIcon)`
    width: 35px;
    height: 35px;
    padding: 7px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 5px;
    margin-left: 5px;
    transition: transform 0.3s;
`;

const StyledEditNewIcon = styled(EditNewIcon)`
    width: 15px;
    height: 15px;
    transition: transform 0.3s;
    cursor: pointer;
`;

const PanelContent = styled.div`
    display: flex;
    width: 100%;
`;

const LeftSide = styled.div`
    width: 60%;
    padding: 15px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
`;

const RightSide = styled.div`
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ThinVL = styled.div`
    width: 1px;
    background: rgba(255, 255, 255, 0.06);
    margin: 0 10px;
`;

const Description = styled.div`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 6px;
    margin-bottom: 12px;
`;

const CardMedia = styled.div`
    margin-top: 10px;
    border-radius: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border: 1px solid rgba(255, 255, 255, 0.1);
`;

const CardImage = styled.img`
    width: 80%;
    height: 150px;
    object-fit: cover;
    border-radius: 10px;
`;
